import { createFileRoute } from "@tanstack/react-router";
import { requireFeature, requireSetting } from "@/lib/feature-gate";

export const Route = createFileRoute("/user/_layout/overtime")({
  beforeLoad: () => {
    requireFeature("attendance");
    requireSetting("overtime_approval_required");
  },
  component: OvertimeRequestPage,
});

import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import { OvertimeRequestList } from "@/components/user/overtime/request-list";
import { OvertimeRequestDialog } from "@/components/user/overtime/OvertimeRequestDialog";
import { useAuthStore } from "@/stores/useAuthStore";
import type { OvertimeRequest } from "@/types/overtime";
import useFetchOvertimeRequests from "@/hooks/api/overtime/useFetchOvertimeRequests";
import useCancelOvertimeRequest from "@/hooks/api/overtime/useCancelOvertimeRequest";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";

function OvertimeRequestPage() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  const requestsQuery = useFetchOvertimeRequests(
    { page, page_size: pageSize },
    { enabled: user != null },
  );
  const requests = requestsQuery.data?.results ?? [];
  const count = requestsQuery.data?.count ?? 0;

  const cancelOvertimeRequest = useCancelOvertimeRequest();
  const isCanceling = cancelOvertimeRequest.isPending;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState<OvertimeRequest | null>(
    null,
  );

  function handleEdit(request: OvertimeRequest) {
    setEditingRequest(request);
    setDialogOpen(true);
  }

  function handleDialogOpenChange(open: boolean) {
    setDialogOpen(open);
    if (!open) setEditingRequest(null);
  }

  function handleView(request: OvertimeRequest) {
    if (request.approval) {
      navigate({
        to: "/user/overtime/$id",
        params: { id: String(request.approval.id) },
      });
    }
  }

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="p-6 pt-0 pb-4">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-foreground">Overtime</h2>
              <p className="text-muted-foreground">
                Submit and track your overtime requests
              </p>
            </div>
            <Button
              onClick={() => setDialogOpen(true)}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              disabled={!user || isCanceling}
            >
              <Plus className="h-4 w-4 mr-2" />
              Request Overtime
            </Button>
          </div>

          <div>
            <OvertimeRequestList
              requests={requests}
              onCancelOvertimeRequest={(id, payload) =>
                cancelOvertimeRequest.mutateAsync({
                  id,
                  remarks: payload.remarks,
                })
              }
              onEditOvertimeRequest={handleEdit}
              onViewOvertimeRequest={handleView}
              isLoading={isCanceling}
            />
          </div>

          <PaginationComponent
            count={count}
            page={page}
            onPageChange={setPage}
            isLoading={requestsQuery.isFetching}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
          />
        </div>

        <OvertimeRequestDialog
          open={dialogOpen}
          onOpenChange={handleDialogOpenChange}
          request={editingRequest ?? undefined}
          onSuccess={() => requestsQuery.refetch()}
        />
      </div>
    </SidebarInset>
  );
}
